'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useQueryClient } from '@tanstack/react-query';
import { useReducedMotion } from 'framer-motion';
import {
  encodeFunctionData,
  parseEventLogs,
  parseUnits,
  zeroAddress,
  type Hex,
} from 'viem';

import { useLaunchRecap } from '../../onboarding/use-onboarding-state';
import { useWallet } from '@/hooks/use-wallet';
import { useTransactionQueue } from '@/hooks/use-transactions';
import {
  HV_TAKEOVER_UGM_ADDRESS,
  isV22OrLaterGrid,
  isV23Grid,
  LAUNCH_UGM_ADDRESS,
} from '@/lib/constants/addresses';
import { UGM_V22_CREATE_GRID_ABI } from '@/lib/contracts';
import { lobbyBoardIdFromGridId, lobbyPathForBoard } from '@/lib/lobby-routes';
import { refreshGridSnapshot } from '@/lib/grid-snapshot-refresh';

import type { LaunchDraft } from './use-launch-wizard';
import { clearPersistedLaunchDraft } from './use-launch-wizard';
import {
  buildMetadataPayload,
  writeInitialGridMetadata,
} from './launch-metadata';
import { clearPendingMetadata, setPendingMetadata } from './pending-metadata';
import {
  clearPendingSeatInit,
  setPendingSeatInit,
} from './pending-seat-init';

// ---------------------------------------------------------------------------
// UGM ABI slices — V21 (legacy) vs V22 (lean fork) launch
// ---------------------------------------------------------------------------
//
// Co-located here (rather than in `lib/contracts/abi.ts`) because the launch
// flow is the only consumer of these selectors. The V21 slice is preserved
// for back-compat with the legacy `HV_TAKEOVER_UGM_ADDRESS` board if
// `LAUNCH_UGM_ADDRESS` ever falls back to it (no V22 deployment configured).
// New launches target V22 — see PLAN_UGM_V22_APP_INDEXER.md §4.6 and Q1.

const UGM_V21_LAUNCH_ABI = [
  {
    name: 'createGrid',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      {
        name: 'params',
        type: 'tuple',
        components: [
          { name: 'taxRateBps', type: 'uint16' },
          { name: 'totalSeats', type: 'uint16' },
          { name: 'taxToken', type: 'address' },
          { name: 'yieldToken', type: 'address' },
          { name: 'seatPrices', type: 'uint128[]' },
        ],
      },
    ],
    outputs: [{ name: 'gridId', type: 'uint256' }],
  },
  {
    name: 'initializeSeats',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'gridId', type: 'uint256' },
      { name: 'prices', type: 'uint128[]' },
    ],
    outputs: [],
  },
] as const;

/**
 * V22 wizard-chosen default Dutch auction window — 30 minutes in seconds.
 *
 * NOT the same as the contract's `DEFAULT_FORFEITURE_DURATION`
 * (10 minutes) — that's the onchain fallback when `Grid.forfeitureDuration`
 * is 0. This constant is the explicit value the wizard passes at
 * `createGrid` time so every newly created board inherits the same
 * tight decay window regardless of the chain default. Stored on the
 * `GridConfig`'s slot-2 packing and surfaced to the UI via
 * `gridConfigV22(gridId)`. Wizard exposes no override.
 *
 * For the seat-data builder's contract-fallback semantic see
 * `V2_CONSTANTS.V22_DEFAULT_FORFEITURE_DURATION` in `lib/types/v2.ts`.
 */
const V22_DEFAULT_FORFEITURE_DURATION = 1_800;

const GRID_CREATED_EVENT_ABI = [
  {
    name: 'GridCreated',
    type: 'event',
    inputs: [
      { name: 'gridId', type: 'uint256', indexed: true },
      { name: 'creator', type: 'address', indexed: true },
    ],
  },
] as const;

// USDC on Base — hardcoded as the only supported tax token for the
// boardroom UGM v2.1 (see plan §8). The guardian must `setAllowedTaxToken`
// with this address before user-created grids can succeed.
//
// String literal must match the EIP-55 checksum exactly — viem 2.x's
// `encodeFunctionData` and address-typed parsers throw
// `InvalidAddressError` on a mixed-case address whose casing doesn't
// match its keccak256 hash. The previous casing
// (`0x833589fCd6EdB6E08f4c7C32D4F71b54bdA02913`) failed checksum
// because positions 9 / 11 / 12 / 14 / 28 were flipped from the
// canonical form Basescan displays.
const USDC_BASE_ADDRESS: Hex = '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913';
// USDC has 6 decimals on Base.
const USDC_DECIMALS = 6;

// Each seat write costs ~115–130k gas in practice (2-slot `SeatInfo` SSTORE
// plus an `EnumerableSet.UintSet.add` for the holder's seat list — measured
// against the V21 deploy script's broadcast at
// `broadcast/30_DeployHvTakeover.s.sol/8453/run-latest.json`, which spent
// ~12.8M gas for a 100-seat batch on Base mainnet). At BATCH_SIZE=100
// that's ~12.8M gas per `initializeSeats` call — comfortably under
// Coinbase Smart Wallet's ~25M simulation cap, MetaMask's ~30M cap, and
// Alchemy's 30M `eth_estimateGas` default. Same size the deploy script
// uses, so we're paving a road we've already proved works.
//
// IMPORTANT: each batch is marked `noBatch: true` on its
// `ActionQueueItem` so `useTransactionQueue` routes it through the
// sequential `eth_sendTransaction` path. Without that flag, ERC-5792's
// `wallet_sendCalls` (atomicRequired:true) would collect every
// consecutive `initializeSeats` call into a single bundle whose
// aggregate gas overflows wallet/bundler/RPC simulation caps — that
// was the actual 256-tile launch failure (4 batches × ~7M gas ≈ 30M
// in one atomic tx, despite the per-batch math looking fine on paper).
// See `use-transactions.tsx`'s batch collector. With `noBatch: true`
// the per-tx cap is the only thing that matters, and 12.8M ≪ 25M.
//
// Trade-off: a 12×12 board (144 seats) becomes 2 wallet prompts
// instead of 5; smaller boards collapse to a single prompt. If the
// per-batch gas budget ever creeps up (extra SSTOREs in a future
// `initializeSeats` rework, calldata growth, etc.) drop this back to
// 64 — anything ≤ ~16M gas/batch still clears CBSW.
const SEAT_INIT_BATCH_SIZE = 100;

function chunk<T>(arr: T[], size: number): T[][] {
  if (size <= 0) return [arr];
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    out.push(arr.slice(i, i + size));
  }
  return out;
}

/**
 * Lifecycle of a board launch.
 *
 *   idle → submitting → warping → done
 *                     ↘
 *                       error
 *
 * `submitting` covers the onchain phase (two sequential transactions:
 * `createGrid` → `initializeSeats`). `warping` is the visual hand-off
 * back to the lobby. `done` is reached the moment we soft-navigate.
 */
export type LaunchGridStatus =
  | 'idle'
  | 'submitting'
  | 'warping'
  | 'done'
  | 'error';

export interface UseLaunchGrid {
  /** Current step in the launch lifecycle. See {@link LaunchGridStatus}. */
  status: LaunchGridStatus;
  /** True while the warp-out + lobby hand-off is animating. */
  warping: boolean;
  /** True while the launch pipeline is mid-flight (submit → warp). The
   *  Review step's hero CTA reads this to disable itself and swap its
   *  label to {@link UseLaunchGrid.currentActionLabel}. */
  isLaunching: boolean;
  /** Human-readable label for the in-flight phase — drives the hero
   *  button copy on the Review step. `null` when idle / errored. */
  currentActionLabel: string | null;
  /** Last error message, if {@link status} is `'error'`. */
  error: string | null;
  /** Trigger the launch. Idempotent while a launch is in flight. */
  launch: (draft: LaunchDraft) => Promise<void>;
}

/**
 * `useLaunchGrid()` is the launch wizard's single submit-side primitive.
 *
 * It owns three concerns the wizard UI shouldn't:
 *
 * 1. **On-chain submit** — encodes a `createGrid(CreateGridParams)` call
 *    against the boardroom UGM v2.1, parses the resulting `GridCreated`
 *    event for the new `gridId`, then calls `initializeSeats(gridId, prices)`
 *    in a follow-up transaction (the contract requires both to actually
 *    expose seats for purchase). Tax token is hardcoded to USDC on Base
 *    and `yieldToken` is `address(0)` — adapters supply the actual yield
 *    stream when the creator attaches them post-launch.
 *
 * 2. **Choreography** — flips `status` to `'warping'` so `LaunchClient`'s
 *    `<LaunchBreath />` overlay can play the amber breath, then schedules
 *    the soft-navigate to the new board. Honors `prefers-reduced-motion`.
 *
 * 3. **Board hand-off** — drops the one-shot `RECAP_PENDING_KEY` flag via
 *    `useLaunchRecap.markLaunched()` so the lobby fires the recap card on
 *    its next mount, and pushes the freshly-launched board's
 *    `/board/<ugm>-<numericId>` URL via Next's router (no full reload).
 *
 * Pulling these out of `LaunchClient` keeps the wizard component a thin
 * step-renderer + footer — exactly what the rest of the lobby features
 * are shaped like — and gives any future "Quick relaunch" / "Duplicate
 * board" entry point a one-call API to drive the same flow.
 */
export function useLaunchGrid(): UseLaunchGrid {
  const router = useRouter();
  const reduceMotion = useReducedMotion();
  const recap = useLaunchRecap();
  const { getAccessToken, isAddressLinked } = useWallet();
  const { executeActions } = useTransactionQueue();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<LaunchGridStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  // Human-readable label of the current launch phase. Surfaced through
  // the public hook return so the Review step's hero CTA can show
  // exactly which transaction the user is being prompted for, and
  // disable itself for the duration of the pipeline.
  const [currentActionLabel, setCurrentActionLabel] = useState<string | null>(null);

  const warpTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlight = useRef(false);
  // Tracks whether the most recent error came from the
  // `!isAddressLinked` defense-in-depth gate (vs. a real
  // transaction / network failure further down the pipeline).
  // Used by the cleanup effect below to clear the gate message
  // automatically once the user signs in — without conflating
  // gate-blocked state with a genuine chain-revert error which the
  // user must explicitly retry past.
  const errorIsGateBlocked = useRef(false);

  useEffect(() => {
    return () => {
      if (warpTimer.current) clearTimeout(warpTimer.current);
    };
  }, []);

  // When the wagmi-active address links into the Privy session, drop
  // the gate-blocked error so the CTA's now-green "Launch board"
  // copy isn't sitting next to a stale red "Sign in first" line.
  // We deliberately scope this to gate-blocked errors (tracked via
  // `errorIsGateBlocked`): a chain-revert error from a separate
  // launch attempt isn't auto-cleared here — that one needs an
  // explicit click on the Launch button to retry.
  useEffect(() => {
    if (isAddressLinked && errorIsGateBlocked.current) {
      errorIsGateBlocked.current = false;
      setError(null);
      setStatus('idle');
    }
  }, [isAddressLinked]);

  const launch = useCallback(
    async (draft: LaunchDraft) => {
      if (inFlight.current) {
        console.warn('[useLaunchGrid] launch() ignored — already in-flight');
        return;
      }
      // Defense-in-depth: the Review step's CTA already swaps to "Sign
      // in with this wallet" when the wagmi-active address isn't part
      // of the current Privy session, but a fast manual click during
      // the gate-render race could still reach this handler. If we ran
      // the onchain `createGrid` here, the follow-up metadata POST
      // would 403 with `Only the grid creator` because the Privy
      // access token is bound to a wallet that didn't sign the tx —
      // exactly the bug we surfaced as `address-set mismatch`. Bail
      // early with a typed message so the wizard surfaces it inline
      // and the breadcrumb hits the same client-error pipeline as the
      // existing 4xx warn path below.
      if (!isAddressLinked) {
        console.warn(
          '[useLaunchGrid] launch() blocked — wagmi-active wallet not linked to Privy session',
        );
        errorIsGateBlocked.current = true;
        setError(
          'Sign in with the wallet you want to launch from before continuing.',
        );
        setStatus('error');
        return;
      }
      inFlight.current = true;
      // Any non-gate error path below is a real failure (chain
      // revert, RPC timeout, server 5xx) — clear the gate-blocked
      // marker so the cleanup effect doesn't auto-dismiss the
      // genuine error when the user happens to relink.
      errorIsGateBlocked.current = false;
      setError(null);
      setCurrentActionLabel(null);

      // Resolve target UGM. Prefer the latest V2 generation (V2.3 if
      // configured, otherwise V2.2) — both share the V22 `createGrid`
      // ABI so the lean-fork launch path covers them simultaneously.
      // Fall back to V21 only if no V22-or-later deployment is wired
      // (e.g. local dev w/o env). Either path lands in
      // `executeActions`, but the V22-or-later branch skips
      // `initializeSeats` entirely (the prices are part of
      // `createGrid` itself).
      const launchAddress = LAUNCH_UGM_ADDRESS || HV_TAKEOVER_UGM_ADDRESS;
      const launchTargetIsV22OrLater = isV22OrLaterGrid(launchAddress);
      const launchTargetIsV23 = isV23Grid(launchAddress);

      // Visible breadcrumb so devs can confirm the CTA reached this handler
      // even when a downstream throw aborts before `executeActions` logs.
      console.info('[useLaunchGrid] launch invoked', {
        sideLength: draft.sideLength,
        startingPriceUsdc: draft.startingPriceUsdc,
        taxRateBps: draft.taxRateBps,
        ugm: launchAddress || '(unset)',
        version: launchTargetIsV23
          ? 'v2.3'
          : launchTargetIsV22OrLater
            ? 'v2.2'
            : 'v2.1',
      });

      try {
        if (!launchAddress) {
          throw new Error(
            'Grid manager address not configured (NEXT_PUBLIC_LAUNCH_UGM_ADDRESS or NEXT_PUBLIC_V22_UGM_ADDRESSES or NEXT_PUBLIC_HV_UGM_ADDRESS)',
          );
        }
        if (draft.sideLength == null) {
          throw new Error('Pick a board size before launching');
        }

        const totalSeats = draft.sideLength * draft.sideLength;
        const seatPrice = parseUnits(draft.startingPriceUsdc, USDC_DECIMALS);
        const seatPrices = Array.from(
          { length: totalSeats },
          () => seatPrice,
        );

        const ugm = launchAddress as Hex;

        // ---- Submit phase --------------------------------------------------
        //
        // V22 / V23: single tx — `createGrid` accepts `initialPrices`
        // directly and lazy-materializes seats on demand, so the V21
        // follow-up `initializeSeats` round-trip disappears entirely.
        // V23 keeps the V22 `CreateGridParams` shape verbatim (the
        // module / extended-hooks surface is layered on after launch
        // via `setApprovedModule`), so the same encoded calldata
        // covers both generations.
        // V21: two sequential txs (createGrid → batched initializeSeats).
        setStatus('submitting');
        setCurrentActionLabel('Creating board…');

        const createCall = launchTargetIsV22OrLater
          ? {
              to: ugm,
              data: encodeFunctionData({
                abi: UGM_V22_CREATE_GRID_ABI,
                functionName: 'createGrid',
                args: [
                  {
                    taxRateBps: draft.taxRateBps,
                    totalSeats,
                    forfeitureDuration: V22_DEFAULT_FORFEITURE_DURATION,
                    taxToken: USDC_BASE_ADDRESS,
                    yieldToken: zeroAddress,
                    initialPrices: seatPrices,
                  },
                ],
              }),
            }
          : {
              to: ugm,
              data: encodeFunctionData({
                abi: UGM_V21_LAUNCH_ABI,
                functionName: 'createGrid',
                args: [
                  {
                    taxRateBps: draft.taxRateBps,
                    totalSeats,
                    taxToken: USDC_BASE_ADDRESS,
                    yieldToken: zeroAddress,
                    seatPrices,
                  },
                ],
              }),
            };

        const createOutcome = await executeActions('Launching on Takeover', [
          {
            id: 'create-grid',
            label: 'Create board',
            description: `Deploy a ${draft.sideLength}×${draft.sideLength} grid`,
            call: createCall,
          },
        ]);

        if (!createOutcome.success) {
          throw new Error('Board creation cancelled or failed');
        }

        const createResult = createOutcome.results.get('create-grid') as
          | {
              hash: Hex;
              receipt: { logs: Array<{ address: string; data: string; topics: string[] }> };
            }
          | undefined;

        if (!createResult) {
          throw new Error('Missing transaction result for createGrid');
        }

        const events = parseEventLogs({
          abi: GRID_CREATED_EVENT_ABI,
          logs: createResult.receipt.logs as unknown as Parameters<
            typeof parseEventLogs<typeof GRID_CREATED_EVENT_ABI>
          >[0]['logs'],
        });
        const gridIdRaw = events[0]?.args.gridId;
        if (gridIdRaw == null) {
          throw new Error('GridCreated event not found in receipt');
        }
        const gridId = gridIdRaw;

        // Composite gridId used everywhere downstream. Computed before
        // the metadata POST so the pending-write key matches the
        // lobby's retry lookup verbatim. Also computed BEFORE the seat
        // init runs (V21 path only) so a partial-failure path can still
        // stash the pending entry for the resume flow.
        const compositeGridId = `${ugm.toLowerCase()}-${gridId.toString()}`;

        if (!launchTargetIsV22OrLater) {
          setCurrentActionLabel('Initializing seats…');

          // V21 path: split into BATCH_SIZE chunks. Each chunk becomes
          // its own `ActionQueueItem` flagged `noBatch: true` so
          // `executeActions` sends each one as a discrete
          // `eth_sendTransaction` instead of collecting them into one
          // ERC-5792 `wallet_sendCalls` bundle — bundling is what
          // bricked 256-seat launches (4 × 64 batches ≈ 30M gas in a
          // single atomic tx, well over wallet/bundler caps).
          // `initializeSeats` is naturally idempotent: it advances
          // `seatsInitialized[gridId]` per call and rejects calls that
          // would exceed `totalSeats`, so a partial-failure resume picks
          // up cleanly from the onchain count.
          //
          // V22 skips this branch entirely — `createGrid` already
          // wrote the per-seat prices into the packed initial-price
          // vector, and seats lazy-materialize on first interaction.
          const seatBatches = chunk(seatPrices, SEAT_INIT_BATCH_SIZE);
          const initActions = seatBatches.map((batch, idx) => {
            const startSeat = idx * SEAT_INIT_BATCH_SIZE;
            const endSeat = startSeat + batch.length - 1;
            return {
              id: `init-seats-${idx}`,
              label:
                seatBatches.length === 1
                  ? 'Initialize seats'
                  : `Initialize seats ${idx + 1}/${seatBatches.length}`,
              description:
                seatBatches.length === 1
                  ? `Write ${totalSeats} starting prices on chain`
                  : `Seats ${startSeat + 1}–${endSeat + 1}`,
              call: {
                to: ugm,
                data: encodeFunctionData({
                  abi: UGM_V21_LAUNCH_ABI,
                  functionName: 'initializeSeats',
                  args: [gridId, batch],
                }),
              },
              noBatch: true,
            };
          });

          // Stash the full seat-prices array to localStorage BEFORE the
          // seat-init batches run. If the user rejects mid-sequence
          // (batch 2 of 4, etc.) the grid is live onchain but
          // `seatsInitialized < totalSeats` and the contract refuses
          // every `acquireSeat` until the rest land. The creator's
          // tools panel uses this entry to drive a "Resume init" CTA
          // which reads the onchain count and submits the remaining
          // batches without restarting the launch flow. Cleared on a
          // successful full init below.
          setPendingSeatInit({
            compositeGridId,
            managerAddress: ugm.toLowerCase(),
            numericId: gridId.toString(),
            seatPrices: seatPrices.map((p) => p.toString()),
            totalSeats,
            createdAt: Date.now(),
          });

          const initOutcome = await executeActions(
            'Launching on Takeover',
            initActions,
          );

          if (!initOutcome.success) {
            // The grid exists onchain (createGrid succeeded) but is
            // partially initialized. The lobby will detect this on the
            // board page and surface a "Resume initialization" CTA — the
            // creator can finish the remaining batches without redoing
            // createGrid. We deliberately throw here so the wizard's
            // error UI renders, but we DO NOT roll back the onchain
            // state (impossible) or clear the pending metadata draft;
            // the creator's plinth/skybox picks survive the partial
            // failure for the resume flow.
            throw new Error(
              'Seat initialization cancelled or failed — your board is live but seats need to be finished before they can be claimed',
            );
          }

          // Full init succeeded — drop the pending seat-init entry so
          // the creator's tools panel doesn't show a stale "Resume init"
          // CTA on next mount.
          clearPendingSeatInit(compositeGridId);
        }

        // Stash the metadata payload in localStorage BEFORE attempting
        // the POST. If anything kills the in-flight request (network
        // blip, expired token, browser crash mid-navigation), the
        // lobby's pending-metadata retry path will pick this up on the
        // next board mount and flush it through with a fresh access
        // token. This is the primary fix for "I picked marble + desert
        // but the board ended up wood + homestead" — that bug is the
        // post-launch metadata write silently failing, leaving the
        // lobby to fall back to adapter-derived defaults.
        const metadataBody = buildMetadataPayload(draft);
        setPendingMetadata({
          compositeGridId,
          managerAddress: ugm.toLowerCase(),
          numericId: gridId.toString(),
          body: metadataBody,
          createdAt: Date.now(),
        });

        // Off-chain metadata — name / description / theming. Auth via the
        // Privy access token; the server enforces creator ownership
        // against the onchain `Grid.creator_id`. `writeInitialGridMetadata`
        // already retries transient failures (404 indexer-lag, 5xx,
        // network) with exponential backoff; here we just surface the
        // outcome.
        setCurrentActionLabel('Saving metadata…');
        const accessToken = await getAccessToken();
        const metadata = await writeInitialGridMetadata({
          managerAddress: ugm,
          gridId,
          draft,
          accessToken,
        });
        if (metadata.ok) {
          // Successful flush — drop the localStorage entry so the lobby
          // doesn't redundantly retry on next mount.
          clearPendingMetadata(compositeGridId);
        } else {
          console.warn(
            '[useLaunchGrid] writeInitialGridMetadata failed; will retry on next board mount:',
            metadata.error,
          );
        }

        // Prime the snapshot React Query cache so the post-navigation
        // board page reads the just-saved name / image / socials on its
        // first paint instead of the pre-write snapshot the server-side
        // route may have served while the metadata POST was in flight.
        // Mirrors `CreatorGridToolsPanel`'s edit-save hand-off so both
        // paths behave the same way.
        if (metadata.ok) {
          const refreshed = await refreshGridSnapshot(
            queryClient,
            compositeGridId,
          ).catch(() => false);
          if (!refreshed) {
            await queryClient
              .invalidateQueries({ queryKey: ['v2-grid', compositeGridId] })
              .catch(() => undefined);
          }
        }

        // ---- Hand-off phase ------------------------------------------------
        //
        // Stash the one-shot recap flag *before* we navigate; the lobby
        // reads it on its next mount and clears it via
        // `useLaunchRecap.clear()`.
        recap.markLaunched();

        // The persisted wizard draft survives full-page navigation
        // (used by mobile Verify-X redirect). Clear it now that the
        // board is committed so the next "Create board" starts blank
        // instead of inheriting this board's identity.
        clearPersistedLaunchDraft();

        setStatus('warping');
        setCurrentActionLabel('Finalizing…');

        const delay = reduceMotion ? 0 : 280;
        await new Promise<void>((resolve) => {
          warpTimer.current = setTimeout(() => {
            warpTimer.current = null;
            resolve();
          }, delay);
        });
        const boardPath = lobbyPathForBoard(
          lobbyBoardIdFromGridId(compositeGridId),
        );
        router.push(boardPath);
        setStatus('done');
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Launch failed';
        // Surface the failure on the console — without this the catch
        // turns every early throw (missing env var, unset sideLength,
        // wallet rejection, RPC error) into a silent no-op CTA.
        console.error('[useLaunchGrid] launch failed:', e);
        setError(message);
        setStatus('error');
        setCurrentActionLabel(null);
      } finally {
        // Always release the in-flight lock — the previous version only
        // reset it on `catch`, so a successful launch (or a navigation
        // that didn't unmount the hook) left subsequent clicks dead.
        inFlight.current = false;
      }
    },
    [
      reduceMotion,
      recap,
      router,
      getAccessToken,
      isAddressLinked,
      executeActions,
      queryClient,
    ],
  );

  return {
    status,
    warping: status === 'warping',
    isLaunching: status === 'submitting' || status === 'warping',
    currentActionLabel,
    error,
    launch,
  };
}
